import React, { useState } from 'react';
import { usePrompts } from '../context/PromptContext';
import { useToast } from '../context/ToastContext';
import { useButtonState } from '../hooks/useButtonState';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiTag, FiHash, FiStar, FiCheck, FiAlertCircle } = FiIcons;

const PlaceholderManager = ({ placeholders, onInsertPlaceholder }) => {
  const { rawVariables, addVariable } = usePrompts();
  const { success: showSuccess, error: showError } = useToast();
  const [newPlaceholder, setNewPlaceholder] = useState('');

  const {
    state: createVarState,
    error: createVarError,
    handleAction: handleCreateVarAction,
    icon: createVarIcon,
    text: createVarText,
    classes: createVarClasses,
  } = useButtonState({
    idleText: 'Create',
    loadingText: 'Creating...',
    successText: 'Created!',
    failedText: 'Failed',
    idleIcon: FiPlus,
    onSuccess: () => {
      showSuccess('Variable created successfully!');
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to create variable';
      showError(errorMsg);
    },
    autoResetDelay: 2000,
  });

  const favouriteVariables = rawVariables
    .filter(v => v.favourite)
    .map(v => v.key);

  const handleAddPlaceholder = async () => {
    if (!newPlaceholder.trim()) return;
    const name = newPlaceholder.trim().replace(/[{}]/g, '');
    const formatted = `{${name}}`;

    await handleCreateVarAction(async () => {
      await addVariable(name, [], true);
      onInsertPlaceholder(formatted);
      setNewPlaceholder('');
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddPlaceholder();
    }
  };

  return (
    <div className="neo-card p-0">
      <div className="neo-window-header bg-neo-purple">
        <div className="flex items-center">
            <SafeIcon icon={FiHash} className="mr-2" />
            <span>Variables</span>
        </div>
      </div>

      <div className="p-4 space-y-5 bg-white">
        <div>
          <label className="block text-xs font-bold text-black mb-2 uppercase">
            Create Variable
          </label>
           <div className="flex gap-2">
             <input
               type="text"
               value={newPlaceholder}
               onChange={(e) => setNewPlaceholder(e.target.value)}
               onKeyDown={handleKeyDown}
               placeholder="Name..."
               className="neo-input text-sm py-1"
             />
             <button
               onClick={handleAddPlaceholder}
               disabled={createVarState === 'loading'}
               className={`neo-btn bg-black text-white px-3 py-1 border-black cursor-pointer transition-all ${createVarClasses}`}
               title={createVarState === 'loading' ? 'Creating...' : 'Create variable and insert'}
             >
               <SafeIcon icon={createVarState === 'loading' ? FiPlus : createVarIcon} size={16} className={createVarState === 'loading' ? 'animate-spin' : ''} />
               <span className="ml-1 text-xs">{createVarText}</span>
             </button>
           </div>
        </div>

        {placeholders.length > 0 && (
          <div>
            <label className="block text-xs font-bold text-black mb-2 uppercase border-b-2 border-black pb-1">
              Detected Variables
            </label>
            <div className="space-y-2">
              {placeholders.map((placeholder, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 border-2 border-black bg-gray-50 shadow-sm"
                >
                  <code className="text-sm font-bold text-blue-600">{placeholder}</code>
                  <button
                    onClick={() => onInsertPlaceholder(placeholder)}
                    className="text-xs font-bold uppercase hover:bg-neo-yellow px-1 border border-transparent hover:border-black transition-all cursor-pointer"
                  >
                    Insert
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs font-bold text-black mb-2 uppercase border-b-2 border-black pb-1">
            Quick Insert <span className="text-neo-yellow ml-2"><SafeIcon icon={FiStar} size={12} /></span>
          </label>
          {favouriteVariables.length === 0 ? (
            <p className="text-xs text-gray-500 font-mono py-2">
              Mark variables as favourite in Variables Manager to see them here
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {favouriteVariables.map((variable) => (
                <button
                  key={variable}
                  onClick={() => onInsertPlaceholder(`{${variable}}`)}
                  className="px-2 py-1 text-xs border-2 border-black bg-white hover:bg-neo-green shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-[1px] active:translate-y-[1px] transition-all font-mono cursor-pointer"
                  title={`Insert {${variable}}`}
                >
                  {'{' + variable + '}'}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaceholderManager;